'use client';

import React from 'react';
import SlideWrapper from '../SlideWrapper';
import { MapPin, Users, Target, Eye } from 'lucide-react';

interface Props {
  id: string;
  registerRef: (id: string, el: HTMLElement | null) => void;
}

const overviewItems = [
{
  icon: <Target size={22} className="text-[#4caf7d]" />,
  label: 'Loại hình',
  value: 'Trại trải nghiệm xanh kết hợp tình nguyện',
  sub: 'Mô hình độc đáo — học qua trải nghiệm thực tế'
},
{
  icon: <Users size={22} className="text-[#e8a030]" />,
  label: 'Đối tượng',
  value: 'Sinh viên năng động',
  sub: 'Yêu thiên nhiên, muốn làm điều có ý nghĩa'
},
{
  icon: <MapPin size={22} className="text-[#a8d87a]" />,
  label: 'Địa điểm',
  value: 'Hồ Trị An, Đồng Nai',
  sub: 'Hệ sinh thái rừng — hồ — thiên nhiên nguyên sơ'
},
{
  icon: <Eye size={22} className="text-[#f5c96a]" />,
  label: 'Kết quả',
  value: 'Giá trị thực tế & có thể nhìn thấy',
  sub: 'Sản phẩm thật, chứng nhận thật, nội dung thật'
}];


export default function Slide03Overview({ id, registerRef }: Props) {
  return (
    <SlideWrapper id={id} registerRef={registerRef} className="bg-[#0f2318]">
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1622557772687-3eab6795a042"
          alt="Hồ Trị An Đồng Nai nhìn từ trên cao, mặt nước xanh bao quanh bởi rừng nhiệt đới nguyên sinh"
          className="w-full h-full object-cover opacity-10" />

        <div className="absolute inset-0 bg-gradient-to-tr from-[#1a3a2a]/60 via-[#0f2318] to-[#0f2318]" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] rounded-full bg-[rgba(168,216,122,0.03)] blur-3xl" />
      </div>

      <div className="relative z-10 max-w-screen-xl mx-auto px-6 lg:px-16 xl:px-24 py-24 pt-28 min-h-screen flex flex-col justify-center">
        <div className="fade-in-up mb-4">
          <div className="chapter-badge">Giới thiệu chương trình</div>
        </div>

        <div className="fade-in-up mb-4">
          <h2 className="font-display font-800 text-[clamp(28px,4.5vw,56px)] leading-[1.1] text-[#f0ede6] tracking-tight">
            Những điều chỉ có tại Tri An Green Travel
          </h2>
        </div>

        <div className="fade-in-up mb-8">
          <div className="section-divider" />
        </div>

        {/* Giới thiệu chương trình paragraphs */}
        <div className="fade-in-up mb-8 space-y-4">
          <p className="font-body text-[15px] lg:text-[16px] text-[#a0b8a8] leading-relaxed">
            Tri An Green Volunteer Camp là chương trình trải nghiệm xanh kết hợp tình nguyện thực tế dành cho sinh viên tại Trị An. Chương trình được xây dựng để mang đến cho sinh viên một hành trình nhiều giá trị hơn một hoạt động ngoại khóa thông thường. Tại đây, các bạn không chỉ được tham gia các hoạt động cộng đồng, mà còn được trải nghiệm, rèn luyện, ghi nhận và trực tiếp tạo ra những giá trị có thể nhìn thấy rõ sau chương trình.
          </p>
          <p className="font-body text-[15px] lg:text-[16px] text-[#a0b8a8] leading-relaxed">
            Sinh viên sẽ tham gia vào các hoạt động thực tế như phục hồi không gian, xây dựng khu trải nghiệm cho các chương trình gia đình phía sau, sản xuất nội dung truyền thông xanh, làm việc nhóm, hoàn thành thử thách và nhận chứng nhận sau hành trình.
          </p>
          <p className="font-body text-[15px] lg:text-[16px] text-[#a0b8a8] leading-relaxed">
            Điểm khác biệt của chương trình nằm ở chỗ: các bạn không chỉ <span className="text-[#f0ede6] font-600">"đi làm tình nguyện"</span>, mà thực sự được bước vào một hành trình có chiều sâu, có kết quả và có cảm xúc, nơi những gì mình làm ra vẫn tiếp tục được sử dụng và tạo giá trị về sau.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 fade-in-up">
          {overviewItems.map((item) =>
          <div
            key={`overview-${item.label}`}
            className="glass-card p-6 lg:p-8 flex items-start gap-5 transition-all duration-300 hover:-translate-y-1 hover:border-[rgba(76,175,125,0.35)]">

              <div className="p-3 rounded-xl bg-[rgba(255,255,255,0.06)] flex-shrink-0">
                {item.icon}
              </div>
              <div>
                <p className="text-[11px] font-600 uppercase tracking-[0.1em] text-[#a0b8a8] font-body mb-1">
                  {item.label}
                </p>
                <p className="font-display font-700 text-[18px] lg:text-[20px] text-[#f0ede6] mb-2">
                  {item.value}
                </p>
                <p className="font-body text-[14px] text-[#a0b8a8] leading-relaxed">{item.sub}</p>
              </div>
            </div>
          )}
        </div>

        <div className="fade-in-up mt-10 amber-card p-6 lg:p-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex-shrink-0 px-4 py-2 rounded-lg bg-[rgba(232,160,48,0.2)] border border-[rgba(232,160,48,0.3)]">
              <span className="font-display font-800 text-[28px] text-gradient-amber tabular-nums">8N7Đ</span>
            </div>
            <div>
              <p className="font-display font-700 text-[18px] text-[#f0ede6]">Trải nghiệm trọn vẹn</p>
              <p className="font-body text-[14px] text-[#a0b8a8] mt-1">Tám ngày bảy đêm tại Trị An — đủ sâu để thay đổi, đủ gọn để phù hợp với lịch học của sinh viên

              </p>
            </div>
          </div>
        </div>
      </div>
    </SlideWrapper>);

}